import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { Fragment } from "react";
import { Icon, Typography } from "@/components/atoms";
import { clsx } from "@/utils";

export interface ModalProps extends React.HTMLAttributes<HTMLDivElement> {
  open: boolean;
  title?: string;
  description?: string;
  onClose?: () => void;
  /** Renders a "< Back" row above the title — for multi-view modals. */
  onBack?: () => void;
  renderButtons?: () => JSX.Element;
  featuredIcon?: JSX.Element;
  variant?: "web-small" | "web-medium" | "web-large" | "mobile";
  headerClassName?: string;
  /**
   * Overrides the dialog's stacking order (default `z-50`). Only needed when
   * layering over another portal that sets its own z-index — e.g. the
   * Chakra-based spreadsheet import wizard, which sits at z-1400.
   */
  zIndexClassName?: string;
  /**
   * Rendered to the right of the title and description, on the same row. For things that belong to
   * the header rather than the body — a wizard's step indicator, say, which reads as chrome and
   * shouldn't push the content down.
   */
  headerAction?: JSX.Element;
  /**
   * Lets a `position: fixed` child fill the viewport, not the dialog — the
   * Veivo walk-around, which goes full-screen on phones and touch devices.
   *
   * A transformed ancestor becomes the containing block of every fixed
   * descendant, and the panel is transformed twice over: its own `transform`
   * class and the slide/scale classes headlessui leaves on it after opening.
   * With this set, the panel only fades, so nothing transforms it.
   */
  allowFullScreenContent?: boolean;
}

export const Modal = ({
  open,
  title,
  description,
  children,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onClose = () => {},
  onBack,
  renderButtons,
  featuredIcon,
  variant = "web-medium",
  className,
  headerClassName,
  zIndexClassName,
  headerAction,
  allowFullScreenContent = false,
}: ModalProps) => {
  const getVariantClasses = () => {
    switch (variant) {
      case "web-large":
        return "w-full max-w-4xl";
      case "web-medium":
        return "w-full max-w-2xl";
      case "mobile":
        return "w-full max-w-sm";
      case "web-small":
      default:
        return "w-full max-w-lg";
    }
  };

  const renderFeaturedIcon = () => {
    if (!featuredIcon) return null;

    return featuredIcon;
  };

  const renderHeader = () => {
    if (!title && !description) return null;

    const heading = (
      <div className={clsx("flex w-full flex-col gap-2", headerClassName)}>
        {title && <Typography variant="title">{title}</Typography>}
        {description && (
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            {description}
          </Typography>
        )}
      </div>
    );

    // Only wrapped when something has to sit beside the heading, so every existing caller keeps the
    // exact markup — and the exact behaviour of headerClassName — it had before.
    if (!headerAction) return heading;

    return (
      <div className="flex w-full items-start justify-between gap-6">
        {heading}
        <div className="shrink-0">{headerAction}</div>
      </div>
    );
  };

  const renderButtonContainer = () => {
    if (!renderButtons) return null;

    if (variant === "mobile") {
      return <div className="flex w-full flex-col gap-3">{renderButtons()}</div>;
    }

    return <div className="flex w-full justify-end gap-3">{renderButtons()}</div>;
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className={clsx("relative", zIndexClassName ?? "z-50")} onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-neutral-mid-gray bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-3">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom={allowFullScreenContent ? "opacity-0" : "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"}
              enterTo={allowFullScreenContent ? "opacity-100" : "opacity-100 translate-y-0 sm:scale-100"}
              leave="ease-in duration-200"
              leaveFrom={allowFullScreenContent ? "opacity-100" : "opacity-100 translate-y-0 sm:scale-100"}
              leaveTo={allowFullScreenContent ? "opacity-0" : "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"}
            >
              <DialogPanel
                className={clsx(
                  "relative rounded-xl bg-white p-6 text-left shadow-lg transition-all",
                  !allowFullScreenContent && "transform",
                  getVariantClasses(),
                  className
                )}
              >
                <div className={clsx("flex flex-col gap-8", variant === "mobile" ? "items-center" : "items-end")}>
                  <div className="flex w-full flex-col gap-5">
                    {onBack && (
                      <button type="button" onClick={onBack} className="flex items-center gap-1 self-start">
                        <Icon name="ArrowLeft2" size="sm" className="text-neutral-black" aria-hidden />
                        <Typography variant="action">Back</Typography>
                      </button>
                    )}

                    {renderFeaturedIcon()}

                    {renderHeader()}

                    {children && <div className="w-full">{children}</div>}
                  </div>

                  {renderButtonContainer()}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
